import { Grid, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { useMemo } from "react";
import {
  NO_REPORT_DISPLAY,
  UNAVAILABLE_DISPLAY,
} from "../reportFieldStates";

// The donut's two slices, named as the operator defined them (task 3141):
// what is still in the concentrated stock, and everything that is not.
// Exported because the client-facing PDF draws the same donut from the same
// two numbers, and two spellings of one slice is how the screen and the paper
// come to disagree about what the reader is looking at.
export const ALLOCATION_SERIES_CONCENTRATION = "Current Concentration";
export const ALLOCATION_SERIES_DIVERSIFIED = "Diversified Assets";

// A whole allocation, in the percent the Diversify card's concentration rows
// and this donut are all expressed in. Exported so the report page's PDF donut
// and its Beginning Concentration default read the SAME 100 rather than each
// keeping a literal of its own (CLAUDE.md's single-home rule).
export const FULL_ALLOCATION_PCT = 100;

/**
 * The donut's two slices for a Current Concentration of `value` percent:
 * the concentration itself, and the Diversified Assets that are 1 - it.
 *
 * **This is where the figure is CLAMPED, and only here** (operator, task
 * 3141). Current Concentration is Bucket 1's ending market value over the
 * three buckets' combined ending total, and that ratio can legitimately
 * exceed 100% — a margin balance or a short position nets against the
 * portfolio's total without netting against Bucket 1's stock. The ROW shows
 * whatever was measured, because capping it would report a concentration
 * nobody calculated; the CHART cannot, because a negative Diversified Assets
 * slice has no arc to draw and the library would either drop it silently or
 * draw a ring that reads as the opposite of the truth. So the chart alone
 * gives up precision, and it gives it up in the honest direction: a fully
 * concentrated ring with nothing diversified.
 *
 * Shared by the screen's donut below and the PDF's, so the two clamp
 * identically rather than one of them drawing a slice the other refuses.
 */
export const allocationSlices = (
  value: number,
): { concentration: number; diversified: number } => {
  const concentration = Math.min(
    Math.max(value, 0),
    FULL_ALLOCATION_PCT,
  );
  return {
    concentration,
    diversified: FULL_ALLOCATION_PCT - concentration,
  };
};

// A donut is an ASSERTION about a split, and it cannot make one about a figure
// that does not exist. `value` is null until a report produces a Current
// Concentration -- or when the run reached the figure and refused it -- and
// drawing it anyway rendered a confident, complete ring claiming 100% stock
// allocation, which is a made-up figure in a chart rather than in a field.
// Same rule as the cards: say there is nothing rather than show a number
// nobody produced.
//
// WHICH "no figure" it says follows reportFieldStates' own ranking, and the
// chart needs `unavailableReason` to obey it: "No Report" means nothing has
// been run yet and running one will fill this in, while a run that REACHED
// the figure and refused it is N/A -- and "N/A outranks both of these
// wherever a reason is present". Told only the null, the chart said "No
// Report" to a refusal and sent the operator off to run a report that would
// change nothing, which is the exact failure that taxonomy exists to stop.
// The reason SENTENCE still belongs to the row beside it: repeating it at
// 100px wide reads as a second, different problem.
const CurrentAllocationChartReport = ({
  value,
  unavailableReason,
}: {
  value: number | null;
  unavailableReason?: string;
}) => {
  const data = useMemo(() => {
    if (value === null) return [];
    const { concentration, diversified } = allocationSlices(value);
    return [
      // `label`, not just `id`: the id identifies the slice to the chart and
      // is never shown, and the tooltip -- the ONLY place a reader can learn
      // which arc is which, the legend being hidden below -- renders the
      // label. Without one it shows a bare "40.00%" against two unnamed
      // colours, which is the donut asserting a split it will not name.
      {
        id: ALLOCATION_SERIES_CONCENTRATION,
        label: ALLOCATION_SERIES_CONCENTRATION,
        value: concentration,
        color: "#00416a",
      },
      {
        id: ALLOCATION_SERIES_DIVERSIFIED,
        label: ALLOCATION_SERIES_DIVERSIFIED,
        value: diversified,
        color: "#dec9a4",
      },
    ];
  }, [value]);

  const settings = {
    margin: { right: 5 },
    width: 100,
    height: 100,
    // `slotProps.legend.hidden`, which is how THIS version of @mui/x-charts
    // (v6) hides a legend -- the `hideLegend` prop that stood here is v7's
    // and v6 ignores it. It happened to look right only because the slices
    // carried no `label` and a legend with nothing to name renders nothing;
    // naming them above would otherwise have put a two-row legend inside a
    // 100px square. The card has no room for one, so the tooltip stays the
    // place the names are read.
    slotProps: { legend: { hidden: true } },
  };
  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          width: "50%",
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: (theme) => theme.palette.primary.dark }}
        >
          Current Allocation
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        {value === null ? (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", alignSelf: "center" }}
          >
            {unavailableReason ? UNAVAILABLE_DISPLAY : NO_REPORT_DISPLAY}
          </Typography>
        ) : (
          <PieChart
            series={[
              {
                innerRadius: 20,
                outerRadius: 40,
                data: data,
                // The legend is hidden to keep the donut the size the card
                // has room for, so the tooltip is the ONLY place a reader
                // learns which arc is which -- it names the slice and its
                // share rather than showing a bare number.
                valueFormatter: (slice: { value: number }) =>
                  `${slice.value.toFixed(2)}%`,
              },
            ]}
            {...settings}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default CurrentAllocationChartReport;
